import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useProjects } from '../../contexts/ProjectContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import StoryStructureOutline from './StoryStructureOutline';
import CharacterManager from './CharacterManager';

const { FiChevronDown, FiChevronRight, FiEdit, FiCheck, FiSave } = FiIcons;

const PlannerWorkspace = ({ project }) => {
  const { updateProject } = useProjects();
  const [activeTab, setActiveTab] = useState('brief');
  const [expandedSections, setExpandedSections] = useState({});
  const [editingOverview, setEditingOverview] = useState(false);
  const [overviewData, setOverviewData] = useState({
    title: '',
    seriesName: '',
    genre: '',
    targetAudience: '',
    wordCountGoal: ''
  });
  const [formData, setFormData] = useState({
    premise: '',
    theme: '',
    faithElement: '',
    storyBeginning: '',
    storyMiddle: '',
    storyEnd: '',
    readerTransformation: '',
    protagonist: '',
    protagonistGoals: '',
    protagonistFlaw: '',
    antagonist: '',
    antagonistMotivations: '',
    supportingCharacters: '',
    setting: '',
    timePeriod: '',
    worldRules: '',
    conflict: '',
    stakes: '',
    spiritualElements: ''
  });
  const [isLoaded, setIsLoaded] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving','saved',null
  const [overviewSaveStatus, setOverviewSaveStatus] = useState(null); // For overview save status
  const [fieldModified, setFieldModified] = useState(false); // Track if any fields have been modified
  const [manualSaveNeeded, setManualSaveNeeded] = useState(false); // Track if manual save is needed

  // Initialize data when project changes
  useEffect(() => {
    if (project) {
      setOverviewData({
        title: project.title || '',
        seriesName: project.series_name || '',
        genre: project.genre || '',
        targetAudience: project.target_audience || '',
        wordCountGoal: project.word_count_goal || ''
      });

      // Initialize form data from project content
      setFormData({
        premise: project.content?.premise || '',
        theme: project.content?.theme || '',
        faithElement: project.content?.faith_element || '',
        storyBeginning: project.content?.story_beginning || '',
        storyMiddle: project.content?.story_middle || '',
        storyEnd: project.content?.story_end || '',
        readerTransformation: project.content?.reader_transformation || '',
        protagonist: project.content?.protagonist || '',
        protagonistGoals: project.content?.protagonist_goals || '',
        protagonistFlaw: project.content?.protagonist_flaw || '',
        antagonist: project.content?.antagonist || '',
        antagonistMotivations: project.content?.antagonist_motivations || '',
        supportingCharacters: project.content?.supporting_characters || '',
        setting: project.content?.setting || '',
        timePeriod: project.content?.time_period || '',
        worldRules: project.content?.world_rules || '',
        conflict: project.content?.conflict || '',
        stakes: project.content?.stakes || '',
        spiritualElements: project.content?.spiritual_elements || ''
      });

      setIsLoaded(true);
      setManualSaveNeeded(false); // Reset manual save flag when project changes
    }
  }, [project]);

  // Calculate progress based on filled fields
  const calculateProgress = (updatedFormData) => {
    // Define all fields to check for progress calculation
    const allFields = [
      // Story Concept fields
      'premise', 'theme', 'faithElement', 'storyBeginning', 'storyMiddle', 'storyEnd', 'readerTransformation',
      // Character fields (legacy - keeping for backward compatibility)
      'protagonist', 'protagonistGoals', 'protagonistFlaw', 'antagonist', 'antagonistMotivations', 'supportingCharacters',
      // Story World fields
      'setting', 'timePeriod', 'worldRules', 'conflict', 'stakes', 'spiritualElements'
    ];

    // Count filled fields
    let filledFields = 0;
    allFields.forEach(field => {
      if (updatedFormData[field] && updatedFormData[field].trim()) {
        filledFields++;
      }
    });

    // Add story structure progress
    const storyStructure = project.content?.story_structure || {};
    const structureFields = [
      'openingScene', 'hookingMoment', 'firstPlotPoint', 'firstPinchPoint', 
      'midpointShift', 'secondPinchPoint', 'secondPlotPoint', 'finalResolution', 'worldBackToNormal'
    ];
    let structureFilledFields = 0;
    structureFields.forEach(field => {
      if (storyStructure[field] && storyStructure[field].trim()) {
        structureFilledFields++;
      }
    });

    // Add character profiles progress
    const characters = project.content?.characters || [];
    const characterProgress = characters.length > 0 ? Math.min(characters.length * 2, 6) : 0; // Max 6 points for characters

    // Total fields = base fields + structure fields + character bonus
    const totalFields = allFields.length + structureFields.length + 6; // 6 max points for characters
    const totalFilledFields = filledFields + structureFilledFields + characterProgress;

    return Math.round((totalFilledFields / totalFields) * 100);
  };

  // Handle form input changes with improved debouncing
  const handleChange = (field, value) => {
    // Update local state immediately
    const updatedFormData = { ...formData, [field]: value };
    setFormData(updatedFormData);
    
    // Mark fields as modified and set manual save flag
    setFieldModified(true);
    setManualSaveNeeded(true);

    // Clear any existing timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Set new timeout for saving - 3 seconds after typing stops
    const timeoutId = setTimeout(async () => {
      try {
        setSaveStatus('saving');
        
        // Calculate progress
        const progress = calculateProgress(updatedFormData);

        // Update project with new data and progress
        await updateProject(project.id, {
          [field]: value,
          progress: progress
        });

        setSaveStatus('saved');
        setManualSaveNeeded(false); // Reset manual save flag after successful auto-save

        // Clear the "saved" status after 2 seconds
        setTimeout(() => {
          setSaveStatus(null);
        }, 2000);
      } catch (error) {
        console.error('Error saving:', error);
        setSaveStatus(null);
      }
    }, 3000); // 3 second delay before auto-save

    setDebounceTimeout(timeoutId);
  };

  // Manual save function for all form data
  const handleManualSave = async () => {
    if (!fieldModified) return; // Don't save if no changes
    
    setSaveStatus('saving');
    
    try {
      // Prepare all field mappings
      const updateData = {};
      Object.keys(formData).forEach(field => {
        updateData[field] = formData[field];
      });
      
      // Calculate progress
      const progress = calculateProgress(formData);
      updateData.progress = progress;
      
      // Update project with all data at once
      await updateProject(project.id, updateData);
      
      setSaveStatus('saved');
      setManualSaveNeeded(false); // Reset manual save flag
      
      // Clear the "saved" status after 2 seconds
      setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error saving:', error);
      setSaveStatus(null);
    }
  };

  const handleOverviewChange = (field, value) => {
    setOverviewData(prev => ({ 
      ...prev, 
      [field]: field === 'wordCountGoal' ? (value ? parseInt(value) : '') : value 
    }));
  };

  const saveOverview = async () => {
    try {
      setOverviewSaveStatus('saving');
      console.log('Saving overview data:', overviewData);

      // Map the form fields to the correct database column names
      const updateData = {
        title: overviewData.title,
        series_name: overviewData.seriesName, // Note: series_name not seriesName
        genre: overviewData.genre,
        target_audience: overviewData.targetAudience, // Note: target_audience not targetAudience
        word_count_goal: overviewData.wordCountGoal ? parseInt(overviewData.wordCountGoal) : null // Note: word_count_goal not wordCountGoal
      };

      console.log('Mapped update data:', updateData);
      await updateProject(project.id, updateData);
      setOverviewSaveStatus('saved');
      setEditingOverview(false);

      // Clear save status after 2 seconds
      setTimeout(() => {
        setOverviewSaveStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error saving overview:', error);
      setOverviewSaveStatus('error');

      // Clear error status after 3 seconds
      setTimeout(() => {
        setOverviewSaveStatus(null);
      }, 3000);
    }
  };

  if (!project || !isLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading project data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Tabs */}
      <div className="flex border-b border-secondary-200 mb-6 overflow-x-auto">
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'brief' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-secondary-600 hover:text-primary-500'}`}
          onClick={() => setActiveTab('brief')}
        >
          Story Concept
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'structure' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-secondary-600 hover:text-primary-500'}`}
          onClick={() => setActiveTab('structure')}
        >
          StoryWise Structure
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'characters' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-secondary-600 hover:text-primary-500'}`}
          onClick={() => setActiveTab('characters')}
        >
          Character Profiles
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'legacy-characters' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-secondary-600 hover:text-primary-500'}`}
          onClick={() => setActiveTab('legacy-characters')}
        >
          Quick Characters
        </button>
        <button
          className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'world' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-secondary-600 hover:text-primary-500'}`}
          onClick={() => setActiveTab('world')}
        >
          Story World
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'brief' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">Story Concept</h2>
            
            <div className="flex items-center space-x-3">
              {saveStatus && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${
                    saveStatus === 'saving' ? 'bg-primary-50 text-primary-600' : 'bg-green-50 text-green-600'
                  }`}
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span className="text-sm">Saving...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      <span className="text-sm">Saved</span>
                    </>
                  )}
                </motion.div>
              )}
              
              {/* Manual save button */}
              {manualSaveNeeded && (
                <button 
                  onClick={handleManualSave}
                  className="flex items-center space-x-2 bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Project Overview */}
            <div className="bg-gradient-to-br from-primary-50 to-spiritual-50 p-6 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-secondary-800">Project Overview</h3>
                <div className="flex items-center space-x-3">
                  {overviewSaveStatus && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`flex items-center space-x-2 px-2 py-1 rounded-lg text-sm ${
                        overviewSaveStatus === 'saving' ? 'bg-primary-100 text-primary-600' : 
                        overviewSaveStatus === 'saved' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                      }`}
                    >
                      {overviewSaveStatus === 'saving' ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
                          <span>Saving...</span>
                        </>
                      ) : overviewSaveStatus === 'saved' ? (
                        <>
                          <SafeIcon icon={FiCheck} className="w-3 h-3" />
                          <span>Saved</span>
                        </>
                      ) : (
                        <span>Error saving</span>
                      )}
                    </motion.div>
                  )}
                  <button
                    onClick={() => setEditingOverview(!editingOverview)}
                    className="text-primary-600 hover:text-primary-700 transition-colors flex items-center space-x-1"
                  >
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    <span className="text-sm">{editingOverview ? 'Cancel' : 'Edit'}</span>
                  </button>
                </div>
              </div>

              {editingOverview ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={overviewData.title}
                      onChange={(e) => handleOverviewChange('title', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                      placeholder="Project title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">Series Name (Optional)</label>
                    <input
                      type="text"
                      value={overviewData.seriesName}
                      onChange={(e) => handleOverviewChange('seriesName', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                      placeholder="e.g., Chronicles of Faith, The Redemption Series"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">Genre</label>
                    <input
                      type="text"
                      value={overviewData.genre}
                      onChange={(e) => handleOverviewChange('genre', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                      placeholder="Story genre"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Target Audience
                    </label>
                    <input
                      type="text"
                      value={overviewData.targetAudience}
                      onChange={(e) => handleOverviewChange('targetAudience', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                      placeholder="Who is your intended reader?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Word Count Goal
                    </label>
                    <input
                      type="number"
                      value={overviewData.wordCountGoal}
                      onChange={(e) => handleOverviewChange('wordCountGoal', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                      placeholder="Target word count"
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={saveOverview}
                      disabled={overviewSaveStatus === 'saving'}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {overviewSaveStatus === 'saving' ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiCheck} className="w-4 h-4" />
                          <span>Save Changes</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-secondary-700">Title:</span>
                    <p className="text-secondary-600">{project.title || 'Not specified'}</p>
                  </div>
                  {project.series_name && (
                    <div>
                      <span className="font-medium text-secondary-700">Series:</span>
                      <p className="text-secondary-600">{project.series_name}</p>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-secondary-700">Genre:</span>
                    <p className="text-secondary-600">{project.genre || 'Not specified'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-secondary-700">Target Audience:</span>
                    <p className="text-secondary-600">{project.target_audience || 'Not specified'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-secondary-700">Word Goal:</span>
                    <p className="text-secondary-600">
                      {project.word_count_goal ? `${project.word_count_goal.toLocaleString()} words` : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-secondary-700">Project Type:</span>
                    <p className="text-secondary-600">{project.type || 'Not specified'}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Story Development Sections */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-secondary-800">One-Line Premise</h3>
              <textarea
                value={formData.premise}
                onChange={(e) => handleChange('premise', e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                placeholder="Write a compelling one-sentence summary of your story..."
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-secondary-800">Central Theme</h3>
              <textarea
                value={formData.theme}
                onChange={(e) => handleChange('theme', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                placeholder="What is the central theme or message of your story?"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-secondary-800">Faith Element</h3>
              <textarea
                value={formData.faithElement}
                onChange={(e) => handleChange('faithElement', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                placeholder="How does faith or spiritual truth appear in your story? What biblical principles are you exploring?"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-secondary-800">Key Story Arc</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Beginning</label>
                  <textarea
                    value={formData.storyBeginning}
                    onChange={(e) => handleChange('storyBeginning', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="How does your story begin?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Middle</label>
                  <textarea
                    value={formData.storyMiddle}
                    onChange={(e) => handleChange('storyMiddle', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="What happens in the middle?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">End</label>
                  <textarea
                    value={formData.storyEnd}
                    onChange={(e) => handleChange('storyEnd', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="How does your story resolve?"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-secondary-800">Reader Transformation</h3>
              <textarea
                value={formData.readerTransformation}
                onChange={(e) => handleChange('readerTransformation', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                placeholder="What do you want readers to think, feel, or do after reading your story?"
              />
            </div>

            {/* Manual Save Button at Bottom */}
            {manualSaveNeeded && (
              <div className="flex justify-end pt-4">
                <button 
                  onClick={handleManualSave}
                  className="flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-spiritual-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-primary-700 hover:to-spiritual-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <SafeIcon icon={FiSave} className="w-5 h-5" />
                  <span>Save All Changes</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'structure' && <StoryStructureOutline project={project} />}
      {activeTab === 'characters' && <CharacterManager project={project} />}
      {activeTab === 'legacy-characters' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">Quick Character Notes</h2>
            
            <div className="flex items-center space-x-3">
              {saveStatus && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${
                    saveStatus === 'saving' ? 'bg-primary-50 text-primary-600' : 'bg-green-50 text-green-600'
                  }`}
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span className="text-sm">Saving...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      <span className="text-sm">Saved</span>
                    </>
                  )}
                </motion.div>
              )}
              
              {/* Manual save button */}
              {manualSaveNeeded && (
                <button 
                  onClick={handleManualSave}
                  className="flex items-center space-x-2 bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Protagonist Section */}
            <div className="border border-secondary-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary-800 mb-4">Protagonist</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Character Name & Background
                  </label>
                  <textarea
                    value={formData.protagonist}
                    onChange={(e) => handleChange('protagonist', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="Describe your protagonist's name, age, background, and key characteristics..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Character Goals & Motivations
                  </label>
                  <textarea
                    value={formData.protagonistGoals}
                    onChange={(e) => handleChange('protagonistGoals', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="What does your protagonist want? What drives them?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Character Flaws & Growth Arc
                  </label>
                  <textarea
                    value={formData.protagonistFlaw}
                    onChange={(e) => handleChange('protagonistFlaw', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="What are their weaknesses? How will they grow throughout the story?"
                  />
                </div>
              </div>
            </div>

            {/* Antagonist Section */}
            <div className="border border-secondary-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary-800 mb-4">Antagonist</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Antagonist Description
                  </label>
                  <textarea
                    value={formData.antagonist}
                    onChange={(e) => handleChange('antagonist', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="Describe your antagonist (person, force, or internal struggle)..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Antagonist Motivations
                  </label>
                  <textarea
                    value={formData.antagonistMotivations}
                    onChange={(e) => handleChange('antagonistMotivations', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="What drives the antagonist? Why do they oppose your protagonist?"
                  />
                </div>
              </div>
            </div>

            {/* Supporting Characters Section */}
            <div className="border border-secondary-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary-800 mb-4">Supporting Characters</h3>
              <textarea
                value={formData.supportingCharacters}
                onChange={(e) => handleChange('supportingCharacters', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                placeholder="Describe key supporting characters, their roles, and how they impact the story..."
              />
            </div>
            
            {/* Manual Save Button at Bottom */}
            {manualSaveNeeded && (
              <div className="flex justify-end pt-4">
                <button 
                  onClick={handleManualSave}
                  className="flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-spiritual-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-primary-700 hover:to-spiritual-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <SafeIcon icon={FiSave} className="w-5 h-5" />
                  <span>Save All Changes</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'world' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">Story World</h2>
            
            <div className="flex items-center space-x-3">
              {saveStatus && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${
                    saveStatus === 'saving' ? 'bg-primary-50 text-primary-600' : 'bg-green-50 text-green-600'
                  }`}
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span className="text-sm">Saving...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      <span className="text-sm">Saved</span>
                    </>
                  )}
                </motion.div>
              )}
              
              {/* Manual save button */}
              {manualSaveNeeded && (
                <button 
                  onClick={handleManualSave}
                  className="flex items-center space-x-2 bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Setting Section */}
            <div className="border border-secondary-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary-800 mb-4">Setting & Time Period</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Primary Location(s)
                  </label>
                  <textarea
                    value={formData.setting}
                    onChange={(e) => handleChange('setting', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="Describe where your story takes place. Include physical details, atmosphere, and mood..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Time Period & Historical Context
                  </label>
                  <textarea
                    value={formData.timePeriod}
                    onChange={(e) => handleChange('timePeriod', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="When does your story take place? What historical or cultural context is important?"
                  />
                </div>
              </div>
            </div>

            {/* World Rules Section */}
            <div className="border border-secondary-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary-800 mb-4">World Rules & Logic</h3>
              <textarea
                value={formData.worldRules}
                onChange={(e) => handleChange('worldRules', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                placeholder="What are the rules of your story world? Include spiritual elements, social structures, or any unique aspects..."
              />
            </div>

            {/* Conflict & Stakes Section */}
            <div className="border border-secondary-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary-800 mb-4">Central Conflict & Stakes</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Main Conflict
                  </label>
                  <textarea
                    value={formData.conflict}
                    onChange={(e) => handleChange('conflict', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="What is the central conflict driving your story?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    What's at Stake?
                  </label>
                  <textarea
                    value={formData.stakes}
                    onChange={(e) => handleChange('stakes', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder="What will happen if your protagonist fails? What are they fighting for?"
                  />
                </div>
              </div>
            </div>

            {/* Spiritual Elements Section */}
            <div className="border border-secondary-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-secondary-800 mb-4">Faith & Spiritual Elements</h3>
              <textarea
                value={formData.spiritualElements}
                onChange={(e) => handleChange('spiritualElements', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                placeholder="How does faith play a role in your story world? What spiritual themes or elements are present?"
              />
            </div>
            
            {/* Manual Save Button at Bottom */}
            {manualSaveNeeded && (
              <div className="flex justify-end pt-4">
                <button 
                  onClick={handleManualSave}
                  className="flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-spiritual-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-primary-700 hover:to-spiritual-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <SafeIcon icon={FiSave} className="w-5 h-5" />
                  <span>Save All Changes</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlannerWorkspace;